import { useCallback, useEffect, useRef } from 'react'

function isVisible() {
  return typeof document === 'undefined' || document.visibilityState === 'visible'
}

/**
 * Schedule ordinary refresh work without hidden-tab or overlapping requests.
 * The caller owns its confirmed data, so refreshes never clear useful content.
 */
export default function useVisibilityRefresh({
  refresh,
  enabled = true,
  intervalMs = null,
  initialRefresh = true,
  initialDelayMs = 0,
  refreshOnFocus = true,
}) {
  const refreshRef = useRef(refresh)
  const enabledRef = useRef(enabled)
  const intervalRef = useRef(intervalMs)
  const mountedRef = useRef(false)
  const timerRef = useRef(null)
  const inFlightRef = useRef(null)
  const controllerRef = useRef(null)
  const runRef = useRef(null)

  refreshRef.current = refresh
  enabledRef.current = enabled
  intervalRef.current = intervalMs

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const schedule = useCallback((delay = intervalRef.current) => {
    clearTimer()
    if (!mountedRef.current || !enabledRef.current || !isVisible()) return
    if (!Number.isFinite(delay) || delay < 0) return
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      void runRef.current?.('interval').catch(() => {})
    }, delay)
  }, [clearTimer])

  const run = useCallback((reason = 'manual') => {
    if (!mountedRef.current || !enabledRef.current) return Promise.resolve(false)
    if (reason !== 'manual' && !isVisible()) return Promise.resolve(false)
    if (inFlightRef.current && !controllerRef.current?.signal.aborted) return inFlightRef.current
    // React StrictMode and dependency changes clean up an effect before
    // mounting its replacement. An aborted predecessor must not suppress the
    // replacement's first refresh; its `finally` is identity-guarded below.
    if (controllerRef.current?.signal.aborted) {
      inFlightRef.current = null
      controllerRef.current = null
    }

    clearTimer()
    const controller = new AbortController()
    controllerRef.current = controller
    const pending = Promise.resolve()
      .then(() => refreshRef.current({ signal: controller.signal, reason }))
      .then(() => true)
      .finally(() => {
        if (inFlightRef.current !== pending) return
        inFlightRef.current = null
        controllerRef.current = null
        if (mountedRef.current && enabledRef.current && isVisible()) schedule()
      })
    inFlightRef.current = pending
    return pending
  }, [clearTimer, schedule])
  runRef.current = run

  useEffect(() => {
    mountedRef.current = true
    const onVisible = () => {
      if (!isVisible()) {
        clearTimer()
        return
      }
      void run('visible').catch(() => {})
    }
    const onFocus = () => {
      if (refreshOnFocus && isVisible()) void run('focus').catch(() => {})
    }

    document.addEventListener('visibilitychange', onVisible)
    if (refreshOnFocus) window.addEventListener('focus', onFocus)

    if (enabled) {
      if (initialRefresh) {
        if (initialDelayMs > 0) schedule(initialDelayMs)
        else void run('initial').catch(() => {})
      } else {
        schedule()
      }
    } else {
      clearTimer()
    }

    return () => {
      mountedRef.current = false
      clearTimer()
      controllerRef.current?.abort()
      document.removeEventListener('visibilitychange', onVisible)
      if (refreshOnFocus) window.removeEventListener('focus', onFocus)
    }
  }, [clearTimer, enabled, initialDelayMs, initialRefresh, intervalMs, refreshOnFocus, run, schedule])

  return useCallback(() => run('manual'), [run])
}
